import React, { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react';
import { useLocation } from 'react-router-dom';

interface PageState {
  data: any;
  scrollPosition: number;
}

interface PagePersistenceContextType {
  getPageState: (path: string) => any;
  setPageState: (path: string, data: any) => void;
  saveScrollPosition: (path: string, position: number) => void;
  getScrollPosition: (path: string) => number;
  getLastVisitedPath: (rootPath: string) => string | undefined;
  setLastVisitedPath: (rootPath: string, fullPath: string) => void;
}

const PagePersistenceContext = createContext<PagePersistenceContextType | undefined>(undefined);

export const PagePersistenceProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [states, setStates] = useState<Record<string, PageState>>({});
  const [lastVisitedPaths, setLastVisitedPaths] = useState<Record<string, string>>({});
  const location = useLocation();

  const getPageState = useCallback((path: string) => {
    return states[path]?.data;
  }, [states]);

  const setPageState = useCallback((path: string, data: any) => {
    setStates(prev => ({
      ...prev,
      [path]: {
        ...prev[path],
        data
      }
    }));
  }, []);

  const saveScrollPosition = useCallback((path: string, position: number) => {
    setStates(prev => ({
      ...prev,
      [path]: {
        ...prev[path],
        scrollPosition: position
      }
    }));
  }, []);

  const getScrollPosition = useCallback((path: string) => {
    return states[path]?.scrollPosition || 0;
  }, [states]);

  const getLastVisitedPath = useCallback((rootPath: string) => {
    return lastVisitedPaths[rootPath];
  }, [lastVisitedPaths]);

  const setLastVisitedPath = useCallback((rootPath: string, fullPath: string) => {
    setLastVisitedPaths(prev => ({
      ...prev,
      [rootPath]: fullPath
    }));
  }, []);

  return (
    <PagePersistenceContext.Provider value={{ 
      getPageState, 
      setPageState, 
      saveScrollPosition, 
      getScrollPosition,
      getLastVisitedPath,
      setLastVisitedPath
    }}>
      {children}
    </PagePersistenceContext.Provider>
  );
};

export const usePagePersistence = () => {
  const context = useContext(PagePersistenceContext);
  if (!context) {
    throw new Error('usePagePersistence must be used within a PagePersistenceProvider');
  }
  return context;
};

/**
 * Hook to persist a specific component's state and scroll position
 */
export function usePersistPageState<T>(key: string, initialState: T) {
  const { getPageState, setPageState, getScrollPosition, saveScrollPosition } = usePagePersistence();
  const [state, _setState] = useState<T>(() => {
    const saved = getPageState(key);
    return saved !== undefined ? saved : initialState;
  });

  const setState = useCallback((value: T | ((prev: T) => T)) => {
    _setState(prev => {
      const next = typeof value === 'function' ? (value as Function)(prev) : value;
      setPageState(key, next);
      return next;
    });
  }, [key, setPageState]);

  // Handle scroll restoration
  useEffect(() => {
    const savedScroll = getScrollPosition(key);
    if (savedScroll > 0) {
      // Small timeout to allow content to render
      const timeoutId = setTimeout(() => {
        window.scrollTo(0, savedScroll);
      }, 50);
      return () => clearTimeout(timeoutId);
    }
  }, [key, getScrollPosition]);

  // Save scroll on unmount or before navigation
  useEffect(() => {
    const handleScroll = () => {
      // We could debounce this, but window scroll is usually not too heavy to read
    };

    window.addEventListener('scroll', handleScroll);
    
    return () => {
      saveScrollPosition(key, window.scrollY);
      window.removeEventListener('scroll', handleScroll);
    };
  }, [key, saveScrollPosition]);

  return [state, setState] as const;
}
